import {
  Ellipsis,
  Maximize2,
  Minimize2,
  Minus,
  PanelBottom,
  PanelLeft,
  PanelRight,
  PictureInPicture2,
  RotateCcw,
} from 'lucide-react';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { MenuButton, type MenuEntry, type MenuPlacement } from '../shell/Menu';
import { strings } from '../strings';
import { ATTR, useDock } from './context';
import type { Placement } from './ids';
import type { ToolWindowBadge, ToolWindowDefinition } from './registry';

const s = strings.dock;

/** The window's body: shows the window's stable host element, into which the page renders the content. */
export function WindowBody({ id }: { id: string }) {
  const dock = useDock();
  const ref = useRef<HTMLDivElement>(null);
  const host = dock.host(id);
  useLayoutEffect(() => {
    const container = ref.current;
    if (!container) return;
    container.appendChild(host);
    return () => {
      if (host.parentNode === container) container.removeChild(host);
    };
  }, [host]);
  return <div ref={ref} data-tool-window-body="" className="tw-body" />;
}

/** The total count on a window's tab (spec 6.8.10: the Problems total). */
export function TabCount({ badge }: { badge: ToolWindowBadge | undefined }) {
  if (!badge || (badge.errors === undefined && badge.warnings === undefined)) return null;
  const errors = badge.errors ?? 0;
  const warnings = badge.warnings ?? 0;
  const tone = errors > 0 ? 'tw-count-danger' : warnings > 0 ? 'tw-count-warning' : 'tw-count-neutral';
  const label = [s.errors(errors), s.warnings(warnings)].join(', ');
  return (
    <span className={`tw-count ${tone}`}>
      <span aria-hidden>{errors + warnings}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

function ControlButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className="tw-ctl-btn" aria-label={label} title={label} onClick={onClick}>
      {children}
    </button>
  );
}

const ICON = 'size-3.5';

/** The ⋯ menu and the quick buttons of a window (spec 6.8.4). */
export function WindowControls({
  definition,
  placement,
  menuPlacement = 'below-end',
}: {
  definition: ToolWindowDefinition;
  placement: Placement;
  menuPlacement?: MenuPlacement;
}) {
  const dock = useDock();
  const { id, title } = definition;
  const narrow = dock.narrow;
  const maximized = placement === 'maximized';
  const shownZone: Placement = maximized
    ? dock.state.layout.windows[id]?.mode === 'floating'
      ? 'floating'
      : (dock.state.layout.windows[id]?.zone ?? 'bottom')
    : placement;

  const actions = {
    left: () => dock.dock(id, 'left'),
    right: () => dock.dock(id, 'right'),
    bottom: () => dock.dock(id, 'bottom'),
    float: () => dock.float(id),
  };
  const narrowReason = narrow ? s.announce.narrow : undefined;
  const entries: MenuEntry[] = [];
  if (shownZone !== 'left' || maximized)
    entries.push({
      kind: 'action',
      id: 'left',
      label: s.menu.dockLeft,
      icon: <PanelLeft aria-hidden className={ICON} />,
      onSelect: actions.left,
      disabledReason: narrowReason,
    });
  if (shownZone !== 'right' || maximized)
    entries.push({
      kind: 'action',
      id: 'right',
      label: s.menu.dockRight,
      icon: <PanelRight aria-hidden className={ICON} />,
      onSelect: actions.right,
      disabledReason: narrowReason,
    });
  if (shownZone !== 'bottom' || maximized)
    entries.push({
      kind: 'action',
      id: 'bottom',
      label: s.menu.dockBottom,
      icon: <PanelBottom aria-hidden className={ICON} />,
      onSelect: actions.bottom,
    });
  if (shownZone !== 'floating' || maximized)
    entries.push({
      kind: 'action',
      id: 'float',
      label: s.menu.float,
      icon: <PictureInPicture2 aria-hidden className={ICON} />,
      onSelect: actions.float,
      disabledReason: narrowReason,
    });
  entries.push(
    {
      kind: 'action',
      id: 'maximize',
      label: maximized ? s.menu.restore : s.menu.maximize,
      icon: maximized ? (
        <Minimize2 aria-hidden className={ICON} />
      ) : (
        <Maximize2 aria-hidden className={ICON} />
      ),
      onSelect: () => dock.toggleMaximize(id),
    },
    {
      kind: 'action',
      id: 'hide',
      label: s.menu.hide,
      icon: <Minus aria-hidden className={ICON} />,
      onSelect: () => dock.hide(id),
    },
    { kind: 'separator', id: 'sep' },
    {
      kind: 'action',
      id: 'reset',
      label: s.menu.reset,
      icon: <RotateCcw aria-hidden className={ICON} />,
      onSelect: () => dock.resetWindow(id),
    },
  );

  const quick: { zone: 'left' | 'right' | 'bottom'; label: string; icon: ReactNode }[] = [];
  const left = {
    zone: 'left' as const,
    label: s.dockLeft(title),
    icon: <PanelLeft aria-hidden className={ICON} />,
  };
  const right = {
    zone: 'right' as const,
    label: s.dockRight(title),
    icon: <PanelRight aria-hidden className={ICON} />,
  };
  const bottom = {
    zone: 'bottom' as const,
    label: s.dockBottom(title),
    icon: <PanelBottom aria-hidden className={ICON} />,
  };
  if (!maximized) {
    if (placement === 'bottom') quick.push(left, right);
    else if (placement === 'left') quick.push(right, bottom);
    else if (placement === 'right') quick.push(left, bottom);
    else if (placement === 'floating') quick.push(bottom, right);
  }

  return (
    <div className="tw-ctl" {...{ [ATTR.controls]: id }}>
      <MenuButton
        buttonClassName="tw-ctl-btn"
        buttonLabel={s.options(title)}
        buttonTitle={s.options(title)}
        buttonContent={<Ellipsis aria-hidden className={ICON} />}
        menuLabel={s.options(title)}
        placement={menuPlacement}
        entries={entries}
      />
      {quick
        .filter((q) => !narrow || q.zone === 'bottom')
        .map((q) => (
          <ControlButton key={q.zone} label={q.label} onClick={() => dock.dock(id, q.zone)}>
            {q.icon}
          </ControlButton>
        ))}
      {!narrow && (placement === 'left' || placement === 'right' || placement === 'bottom') && (
        <ControlButton label={s.float(title)} onClick={() => dock.float(id)}>
          <PictureInPicture2 aria-hidden className={ICON} />
        </ControlButton>
      )}
      <ControlButton
        label={maximized ? s.restore(title) : s.maximize(title)}
        onClick={() => dock.toggleMaximize(id)}
      >
        {maximized ? <Minimize2 aria-hidden className={ICON} /> : <Maximize2 aria-hidden className={ICON} />}
      </ControlButton>
      <ControlButton label={s.hide(title)} onClick={() => dock.hide(id)}>
        <Minus aria-hidden className={ICON} />
      </ControlButton>
    </div>
  );
}
